import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import type { AnalysisConfigSummary, ConfigActivation } from '../model.js';

/**
 * The active configuration: the one Beon analyses with when nobody chose another.
 *
 * The platform has at most one — upstream keeps it so with a unique index and a single writer —
 * and it is a move, never a toggle: activating one puts the previous one's star out, and there is
 * no way to switch the last one off. What lives here is what every screen needs to say about it
 * the same way: which one it is, how a picker shows it, and what the star button may do.
 */

/**
 * The star in a native `<option>`, which has no room for an icon.
 *
 * A character and not a colour, so it survives every theme and a screen reader still names it.
 */
export const ACTIVE_MARK = '★';

type Starred = Pick<AnalysisConfigSummary, 'isActive'>;

/** The active configuration among these, or null — a fresh platform has none until one is saved. */
export function activeConfigOf<T extends Starred>(
  configs: readonly T[] | null | undefined,
): T | null {
  return configs?.find((config) => config.isActive) ?? null;
}

/**
 * The active one first, the rest in the order they came.
 *
 * A picker's first option is the one read first and the one a keyboard lands on: it should be the
 * configuration Beon uses, whatever order the picker sorts the others in.
 */
export function activeFirst<T extends Starred>(configs: readonly T[]): T[] {
  return [
    ...configs.filter((config) => config.isActive),
    ...configs.filter((config) => !config.isActive),
  ];
}

/** «★ Nombre · v3» for the active one, «Nombre · v3» for the rest. */
export function configOptionLabel(
  config: Pick<AnalysisConfigSummary, 'name' | 'version' | 'isActive'>,
  options: { version?: boolean } = {},
): string {
  const withVersion = options.version ?? true;
  const name =
    withVersion && config.version !== null ? `${config.name} · v${config.version}` : config.name;

  return config.isActive ? `${ACTIVE_MARK} ${name}` : name;
}

/**
 * The configuration a picker opens on.
 *
 * What the caller asked for when it is on offer (a group created from a configuration's screen),
 * else the active one, else the first — which is what every picker chose before there was a star,
 * and only happens on a platform with none active yet.
 */
export function defaultConfigId(
  candidates: readonly Pick<AnalysisConfigSummary, 'id' | 'isActive'>[],
  preferred?: number | null,
): number | undefined {
  const asked = candidates.find((config) => config.id === preferred);

  return (asked ?? activeConfigOf(candidates) ?? candidates[0])?.id;
}

/**
 * Whether reading the active configuration failed because there is none.
 *
 * The API answers `VP_CONFIG_NOT_FOUND` on that read only for «none is active», and maps any other
 * 404 to `VP_UNAVAILABLE`. The two states need different screens: one is an invitation to activate
 * a configuration, the other is Virtual Profiler being down.
 */
export function isNoActiveConfig(error: unknown): boolean {
  return error instanceof ApiError && error.code === ApiErrorCode.VP_CONFIG_NOT_FOUND;
}


export interface ActivationStar {
  /** Lit: this is the active one. */
  active: boolean;
  /** The button's accessible name, and its tooltip while it can be pressed. */
  label: string;
  /** Why it cannot be pressed, said in its tooltip instead of the label; null when it can. */
  locked: string | null;
}

/**
 * What the star button of one configuration is.
 *
 * The active one's star cannot be put out: there is always one active, so the way to move it is
 * to activate another, and its tooltip says exactly that. The rest are refused before the API
 * would: without the permission, or never saved with engines (Beon would have nothing to analyse
 * with, and upstream refuses it).
 */
export function activationStar(
  config: Pick<AnalysisConfigSummary, 'isActive' | 'runnable'>,
  context: { canConfigure: boolean; pending: boolean },
): ActivationStar {
  if (config.isActive) {
    return {
      active: true,
      label: 'Configuración activa',
      locked: 'Configuración activa. Para cambiarla, activa otra.',
    };
  }

  const locked = !context.canConfigure
    ? 'Te falta el permiso para configurar'
    : !config.runnable
      ? 'Sin versión guardada no se puede activar'
      : context.pending
        ? 'Activando…'
        : null;

  return { active: false, label: 'Activar para toda la plataforma', locked };
}

/**
 * The question before activating: it changes what Virtual Profiler analyses with, including the interviews
 * already waiting for their first analysis, so it is asked and not done on a click.
 */
export function activationPrompt(
  config: Pick<AnalysisConfigSummary, 'name'>,
  previous: Pick<AnalysisConfigSummary, 'name'> | null,
): { title: string; description: string } {
  const effect =
    'Virtual Profiler analizará con ella toda entrevista sin configuración propia, también las pendientes.';

  return {
    title: `¿Activar «${config.name}»?`,
    description: previous ? `${effect} «${previous.name}» deja de ser la activa.` : effect,
  };
}

/** What the toast says after activating: a repeat is not reported as a move. */
export function activationToast(activation: ConfigActivation, name: string): string {
  return activation.changed
    ? `«${name}» es ahora la configuración activa`
    : `«${name}» ya era la configuración activa`;
}

/** Why the active configuration cannot be deleted, said on the button before the API says it. */
export const ACTIVE_DELETE_REFUSAL = 'Es la configuración activa: activa otra antes de borrarla';
